import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { z } from "zod";

import { CindraButton } from "cindra/CindraButton";
import { CindraPatientInfoPanel, type PatientInfoSection } from "cindra/CindraPatientInfoPanel";
import { getAuthToken } from "services/AccountServices";
import { getSelectedAgent } from "cindra/MastraClient";
import { parsePatientInfoMarkdown } from "cindra/CindraPatientInfoUtils";
import { useMCP } from "mcp/hooks/useMCP";
import { useNotificationManager } from "notifications/NotificationManager";

// Toggle between LLM-mediated and direct MCP tool call methods
// Set to true for direct tool calls (faster, cheaper, bypasses LLM)
// Set to false for LLM-mediated calls (uses agent to call MCP tool)
const USE_DIRECT_TOOL_CALL = true;

// Debug logging flags
const ENABLE_DEBUG_LOGS = false;
const ENABLE_TIMING_LOGS = false;

// Structured output schema for LLM response
const PatientOverviewSchema = z.object({
    markdown: z.string().describe("The formatted markdown content containing patient information")
});

interface CindraPatientOverviewProps {
    patientId: number;
    patientFirstName: string;
    patientLastName: string;
}

// Re-export the type for backward compatibility
export type { PatientInfoSection };

export const CindraPatientOverview: React.FC<CindraPatientOverviewProps> = ({
    patientId,
    patientFirstName,
    patientLastName
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [parsedSections, setParsedSections] = useState<PatientInfoSection[]>([]);
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    const [panelMaxHeight, setPanelMaxHeight] = useState<string>('400px');
    const buttonRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const notificationManager = useNotificationManager();
    const mcp = useMCP();

    const patientFullName = `${patientFirstName} ${patientLastName}`;

    // Extract markdown from conversational text
    // Looks for common conversational prefixes and removes them
    const extractMarkdownFromConversationalText = (text: string): string => {
        // Common conversational patterns to strip
        const conversationalPatterns = [
            /^Here is (?:the )?(?:comprehensive )?information for patient [^:]+:\s*/i,
            /^Here's (?:the )?(?:comprehensive )?information for patient [^:]+:\s*/i,
            /^(?:I've retrieved|I found) (?:the )?(?:comprehensive )?information for patient [^:]+:\s*/i,
            /^(?:The )?(?:Patient )?(?:information|overview|details)(?: for [^:]+)?:\s*/i,
        ];

        let cleanedText = text;

        // Try to remove conversational prefix
        for (const pattern of conversationalPatterns) {
            cleanedText = cleanedText.replace(pattern, '');
        }

        // Remove any trailing conversational text (like "If you need further details...")
        const trailingPatterns = [
            /\n+If you (?:need|have) (?:further|any) (?:details|questions)[^\n]*$/i,
            /\n+Feel free to (?:ask|let me know)[^\n]*$/i,
            /\n+Let me know if you (?:need|have)[^\n]*$/i,
        ];

        for (const pattern of trailingPatterns) {
            cleanedText = cleanedText.replace(pattern, '');
        }

        return cleanedText.trim();
    };

    // Calculate popover position and size relative to the patients panel
    const calculatePopoverStyle = (): React.CSSProperties => {
        const patientsPanelContainer = document.getElementById('patients-panel-container');

        if (!patientsPanelContainer || !buttonRef.current) {
            return {
                top: '100px',
                left: '100px',
                minWidth: '500px',
                maxWidth: '700px',
                maxHeight: '500px',
            };
        }

        // Get panel and button positions
        const panelRect = patientsPanelContainer.getBoundingClientRect();
        const buttonRect = buttonRef.current.getBoundingClientRect();

        // Find the table header row to position below it
        const tableHeader = patientsPanelContainer.querySelector('thead tr');

        // Calculate position relative to panel (not viewport)
        // Top: position below table header
        let top = 100; // Default offset from panel top
        if (tableHeader) {
            const headerRect = tableHeader.getBoundingClientRect();
            // Calculate relative to panel top
            top = headerRect.bottom - panelRect.top + 10; // 10px below header
        }

        // Left: position to the right of the button + margin
        // Calculate button position relative to panel
        const left = buttonRect.right - panelRect.left + 4; // 4px margin

        // Max width: constrained by panel width minus the left position
        const maxWidth = panelRect.width - left - 20; // 20px margin from right edge

        // Max height: constrained by panel height from top position
        const maxHeight = panelRect.height - top - 20; // 20px margin from bottom

        // Store the maxHeight for use in CindraPatientInfoPanel
        setPanelMaxHeight(`${maxHeight}px`);

        return {
            top: `${top}px`,
            left: `${left}px`,
            minWidth: '500px',
            maxWidth: `${maxWidth}px`,
        };
    };

    // Update popover position on window resize
    useEffect(() => {
        if (!showPopover) return;

        const updatePosition = () => {
            setPopoverStyle(calculatePopoverStyle());
        };

        // Initial calculation
        updatePosition();

        // Update on resize (no need for scroll listener since popover is now absolutely positioned within panel)
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, [showPopover]);

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                showPopover &&
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setShowPopover(false);
            }
        };

        if (showPopover) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showPopover]);

    // Fetch patient overview using LLM-mediated approach
    const fetchPatientOverviewViaLLM = async (): Promise<string> => {
        if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Starting LLM-mediated fetch');
        const startTime = ENABLE_TIMING_LOGS ? performance.now() : 0;

        const authToken = getAuthToken();

        // Get the currently selected agent based on cookie preference
        const selectedAgent = getSelectedAgent();

        // Build an optimized prompt that encourages single-step execution
        const prompt = `[auth_token: ${authToken}]

Call the get_patient_information tool with full_name="${patientFullName}" and return ONLY the markdown field from its response. Do not add explanations, formatting, or conversational text - just return the raw markdown content.`;

        // Call the selected agent with the prompt
        let result: { text?: string; object?: any } | null;

        const agentCallStart = ENABLE_TIMING_LOGS ? performance.now() : 0;
        if (typeof selectedAgent.generateVNext === "function") {
            result = await selectedAgent.generateVNext(
                prompt,
                {
                    maxSteps: 2
                }
            );
        }
        else {
            result = await selectedAgent.generate(
                {
                    messages: [{ role: "user", content: prompt }],
                    maxSteps: 2
                }
            );
        }

        if (ENABLE_TIMING_LOGS) {
            const agentCallTime = performance.now() - agentCallStart;
            console.log('[CindraPatientOverview] [TIMING] Agent call took', agentCallTime, 'ms');
        }

        if (ENABLE_DEBUG_LOGS) {
            console.log('[CindraPatientOverview] Full result:', result);
            console.log('[CindraPatientOverview] Result keys:', result ? Object.keys(result) : 'null');
            console.log('[CindraPatientOverview] result.text:', result?.text);
            console.log('[CindraPatientOverview] result.toolResults:', (result as any)?.toolResults);
            console.log('[CindraPatientOverview] result.steps:', (result as any)?.steps);
            console.log('[CindraPatientOverview] result.response:', (result as any)?.response);
        }

        // Try to get markdown directly from tool results first (fastest path, bypasses LLM formatting)
        const toolResults = (result as any)?.toolResults;
        if (toolResults && toolResults.length > 0) {
            const toolResult = toolResults[0];
            // Check for markdown in the structured response
            const markdown = toolResult?.payload?.result?.structuredContent?.markdown;
            if (markdown) {
                if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Total LLM fetch took', performance.now() - startTime, 'ms');
                if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Found markdown directly in tool results (bypassed LLM formatting)');
                return markdown;
            }
        }

        // Fallback to text response from LLM
        if (result?.text) {
            if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Got response with text');

            // Try to parse as JSON first (in case MCP tool returns structured { markdown: "..." })
            try {
                const parsed = JSON.parse(result.text);
                if (parsed.markdown) {
                    if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Found markdown in parsed JSON');
                    if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Total LLM fetch took', performance.now() - startTime, 'ms');
                    return parsed.markdown;
                }
                else if (parsed.error) {
                    throw new Error(parsed.error);
                }
            }
            catch (parseError) {
                // JSON parsing failed - LLM returned plain text
                if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Not JSON, treating as markdown directly');
            }

            // Use the response directly as markdown
            const text = result.text.trim();
            if (text) {
                const cleanedMarkdown = extractMarkdownFromConversationalText(text);
                if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Total LLM fetch took', performance.now() - startTime, 'ms');
                if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Using extracted markdown');
                return cleanedMarkdown;
            }
            else {
                throw new Error("Empty response from agent");
            }
        }
        else {
            throw new Error("No response generated from agent");
        }
    };

    // Fetch patient overview using direct MCP tool call (faster, cheaper, bypasses LLM)
    const fetchPatientOverviewDirectly = async (): Promise<string> => {
        const authToken = getAuthToken();

        if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Starting direct MCP tool call');
        const startTime = ENABLE_TIMING_LOGS ? performance.now() : 0;

        // Call the MCP tool directly using the useMCP hook
        // This bypasses the LLM entirely and calls the MCP server directly
        const result = await mcp.callTool('get_patient_information', {
            auth_token: authToken,
            full_name: patientFullName
        });

        const mcpCallTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
        if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] MCP tool call completed in', mcpCallTime - startTime, 'ms');
        if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Direct MCP tool call result:', result);

        // Extract content from the MCP response
        if (result.content && Array.isArray(result.content)) {
            // MCP tools return content as an array of content items
            for (const item of result.content) {
                if (item.type === 'text' && item.text) {
                    // The MCP tool returns JSON with a markdown field (structured output)
                    const jsonParseStart = ENABLE_TIMING_LOGS ? performance.now() : 0;

                    try {
                        const response = JSON.parse(item.text);
                        if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] JSON parse took', performance.now() - jsonParseStart, 'ms');

                        // Check for markdown field (structured output)
                        if (response.markdown) {
                            if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Total direct call took', performance.now() - startTime, 'ms');
                            if (ENABLE_DEBUG_LOGS) console.log('[CindraPatientOverview] Using markdown from direct MCP call (structured output)');
                            return response.markdown;
                        }
                        else if (response.error) {
                            throw new Error(response.error);
                        }
                        else {
                            console.warn('[CindraPatientOverview] MCP response missing markdown field:', response);
                            throw new Error("MCP tool response missing markdown field");
                        }
                    } catch (parseError) {
                        console.error('[CindraPatientOverview] Failed to parse MCP response as JSON:', parseError);
                        throw new Error(`Invalid JSON response from MCP tool: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
                    }
                }
            }
        }

        throw new Error("Invalid response from MCP tool - no text content found");
    };

    const handleButtonClick = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click navigation

        if (showPopover) {
            setShowPopover(false);
            return;
        }

        if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Button clicked, starting fetch');
        const overallStartTime = ENABLE_TIMING_LOGS ? performance.now() : 0;

        setIsLoading(true);

        try {
            // Fetch markdown using the selected method
            const fetchStartTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
            const markdown = USE_DIRECT_TOOL_CALL
                ? await fetchPatientOverviewDirectly()
                : await fetchPatientOverviewViaLLM();

            const fetchEndTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
            if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Fetch completed in', fetchEndTime - fetchStartTime, 'ms');

            const parseStartTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
            const parsed = parsePatientInfoMarkdown(markdown);
            const parseEndTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
            if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Markdown parsing took', parseEndTime - parseStartTime, 'ms');

            const uiUpdateStart = ENABLE_TIMING_LOGS ? performance.now() : 0;
            setParsedSections(parsed);
            setPopoverStyle(calculatePopoverStyle());
            setShowPopover(true);
            const uiUpdateEnd = ENABLE_TIMING_LOGS ? performance.now() : 0;
            if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] UI update (setState) took', uiUpdateEnd - uiUpdateStart, 'ms');

            if (ENABLE_TIMING_LOGS) console.log('[CindraPatientOverview] [TIMING] Total handleButtonClick took', performance.now() - overallStartTime, 'ms');
        }
        catch (error) {
            console.error("[CindraPatientOverview] Error fetching patient overview:", error);

            // Determine error message
            let errorMessage = "Failed to fetch patient overview. ";
            if (error instanceof Error) {
                const lowerMessage = error.message.toLowerCase();
                if (lowerMessage.includes("api key") || lowerMessage.includes("unauthorized")) {
                    errorMessage += "API key configuration issue.";
                }
                else if (lowerMessage.includes("network") || lowerMessage.includes("connection")) {
                    errorMessage += "Network connection issue.";
                }
                else if (lowerMessage.includes("token") || lowerMessage.includes("auth")) {
                    errorMessage += "Authentication issue.";
                }
                else {
                    errorMessage += error.message;
                }
            }
            else {
                errorMessage += "Unknown error occurred.";
            }

            notificationManager.addError(errorMessage, { duration: 5 });
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleClosePopover = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowPopover(false);
    };

    // Cindra button icon (lightning bolt matching the ModularHeader style)
    const cindraIcon = (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
    );

    // Get the panel container for portal rendering
    const patientsPanelContainer = document.getElementById('patients-panel-container');

    // Render popover content
    const renderPopover = () => {
        if (!showPopover || parsedSections.length === 0 || !patientsPanelContainer) {
            return null;
        }

        return ReactDOM.createPortal(
            <div
                ref={popoverRef}
                className="absolute z-50"
                style={popoverStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <CindraPatientInfoPanel
                    maxHeight={panelMaxHeight}
                    patientName={patientFullName}
                    sections={parsedSections}
                    showHeader={true}
                    // Callbacks
                    onClose={handleClosePopover}
                />
            </div>,
            patientsPanelContainer
        );
    };

    return (
        <>
            <CindraButton
                color="blue"
                icon={cindraIcon}
                isLoading={isLoading}
                ref={buttonRef}
                statusText="Thinking..."
                style="micro"
                title="Get Cindra patient overview"
                // Callbacks
                onClick={handleButtonClick}
            />
            {renderPopover()}
        </>
    );
};
